import { useState, type FormEvent } from 'react'
import type { FulfillmentTarget, OrderDetailResponse, OrderLineView, SupplierItem, UpdateLineTrackingRequest } from '@fitting-room/contracts'
import { Check, Pencil, Undo2 } from 'lucide-react'
import { Link } from 'react-router'
import {
  useAdvanceOrderLine,
  useAssignees,
  useLinkSupplierItem,
  useOverridePickup,
  useUndoOrderLine,
  useUpdateLineTracking,
} from '../../../lib/adminQueries'
import { assigneeName } from '../../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDateTime, boutiqueDayOf, boutiqueToday } from '../../../lib/boutique'
import { formatDay } from '../../../lib/format'
import {
  ADVANCE_LABEL,
  STEP_DAY_LABEL,
  STEP_PHRASE,
  etaOf,
  isStockLine,
  isSupplierLine,
  nextStep,
  previousStep,
  stepStamps,
} from '../../../lib/fulfillment'
import { formatCents } from '../../../lib/orders'
import { useStaffToken } from '../../../lib/staffToken'
import { uiStrings } from '../../../lib/strings'
import { DatePicker } from '../DatePicker'
import { FormField, FormSheet } from '../FormSheet'
import { PaymentStateBadge } from '../payments/PaymentStateBadge'
import { RecordPaymentDialog } from '../payments/RecordPaymentDialog'
import { JovitaItemField } from './JovitaItemField'
import { FulfillmentBadge } from './OrderStatusBadge'
import { ReasonDialog } from './ReasonDialog'

/**
 * A line on its way to the customer, under the line itself: tperent's
 * per-item pickup and its reasoned undo (`PhaseRental`'s `ItemRentalStatus`),
 * for both of this boutique's paths — made to order through the supplier,
 * off the rack from the hold. `docs/22-back-office.md` § "Pickup and arrival".
 *
 * Where it is, the day each step was reached, the supplier's number and the
 * ETA that counts (the one typed here, else the supplier link's, which is
 * shown but never edited here), then one button for the next step and an
 * undo for the last. Nothing for an alteration: it doesn't go home as a thing.
 */
export function LineFulfillment({ detail, line }: { readonly detail: OrderDetailResponse; readonly line: OrderLineView }) {
  const { token } = useStaffToken()
  const members = useAssignees(token).data?.members ?? []
  const [advancing, setAdvancing] = useState<FulfillmentTarget | null>(null)
  const [undoing, setUndoing] = useState(false)
  const [tracking, setTracking] = useState(false)
  const undo = useUndoOrderLine(token, detail.order.id, line.id)
  const t = uiStrings(BOUTIQUE_LOCALE)
  if (line.fulfillment === 'none') return null

  // Canceled, it is read-only; completed, the undo still works — it is how a completed order opens again.
  const live = detail.order.status !== 'canceled'
  const next = live ? nextStep(line) : null
  const back = live ? previousStep(line) : null
  const supplier = isSupplierLine(line)
  const eta = etaOf(line)
  const reached = stepStamps(line).filter((stamp) => stamp.day)
  const editable = live && line.fulfillment !== 'picked_up'

  return (
    <div className="st-fulfillment-block w-full" data-fulfillment={line.fulfillment} data-testid="line-fulfillment">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <FulfillmentBadge fulfillment={line.fulfillment} />
        {/* Beside the step, not instead of it: an imported line still reads Ordered, and that it has no number is the news. */}
        {line.notOrdered ? (
          <span className="st-badge st-alert-kind" data-severity="critical" data-testid="line-not-ordered" title={t.orderNotOrderedTagHint}>
            {t.orderNotOrdered}
          </span>
        ) : null}
        {reached.map((stamp) => (
          <span key={stamp.step} className="st-meta tabular-nums">
            {stamp.label} {formatDay(stamp.day as string, BOUTIQUE_LOCALE)}
          </span>
        ))}
        {line.fulfillment === 'picked_up' && line.pickedUpById ? (
          <span className="st-meta">by {assigneeName(members, line.pickedUpById) ?? 'a former team member'}</span>
        ) : null}
      </div>

      {supplier || line.heldUntil || line.supplierLink ? (
        <dl className="st-meta mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
          {supplier ? (
            <div className="flex gap-1">
              <dt>Supplier order</dt>
              <dd className="tabular-nums text-[color:var(--st-ink)]">{line.supplierOrderNo || '—'}</dd>
            </div>
          ) : null}
          {supplier ? (
            <div className="flex gap-1" data-testid="line-eta">
              <dt>ETA</dt>
              <dd className="tabular-nums text-[color:var(--st-ink)]">
                {eta ? formatDay(eta.day, BOUTIQUE_LOCALE) : '—'}
                {eta?.source === 'manual' && line.systemEta ? (
                  <span className="st-meta"> (supplier says {formatDay(line.systemEta, BOUTIQUE_LOCALE)})</span>
                ) : eta?.source === 'system' ? (
                  <span className="st-meta"> (from the supplier)</span>
                ) : null}
              </dd>
            </div>
          ) : null}
          {line.supplierLink ? (
            <div className="flex gap-1" data-testid="line-jovita">
              <dt>Jovita</dt>
              <dd className="text-[color:var(--st-ink)]">
                <Link to="/admin/orders/jovita" className="tabular-nums underline-offset-2 hover:underline">
                  #{line.supplierLink.itemNo ?? line.supplierLink.itemId}
                </Link>
                {[line.supplierLink.status, line.supplierLink.shippingStatus, line.supplierLink.trackingNo ? `FedEx ${line.supplierLink.trackingNo}` : null]
                  .filter(Boolean)
                  .map((part) => ` · ${part}`)
                  .join('')}
                <span className="st-meta">
                  {line.supplierLink.missingSince
                    ? ' · no longer listed by Jovita'
                    : ` · checked ${boutiqueDateTime(line.supplierLink.syncedAt)}`}
                </span>
              </dd>
            </div>
          ) : supplier && line.fulfillment !== 'picked_up' && live ? (
            <div className="flex gap-1">
              <dt>Jovita</dt>
              <dd>
                <Link to="/admin/orders/jovita" className="underline-offset-2 hover:underline">
                  Link to Jovita
                </Link>
              </dd>
            </div>
          ) : null}
          {line.heldUntil ? (
            <div className="flex gap-1">
              <dt>Held until</dt>
              <dd className="tabular-nums text-[color:var(--st-ink)]">{formatDay(line.heldUntil, BOUTIQUE_LOCALE)}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {live ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {next ? (
            <button type="button" className="st-btn st-btn-sm" onClick={() => setAdvancing(next)}>
              <Check size={13} aria-hidden />
              {ADVANCE_LABEL[next]}
            </button>
          ) : null}
          {editable ? (
            <button type="button" className="st-btn-ghost st-btn-sm" onClick={() => setTracking(true)}>
              <Pencil size={13} aria-hidden />
              {supplier ? 'Supplier & ETA' : 'Held until'}
            </button>
          ) : null}
          {back ? (
            <button
              type="button"
              className="st-btn-quiet st-btn-sm"
              aria-label={`Undo ${STEP_PHRASE[line.fulfillment]} for ${lineName(line)}`}
              onClick={() => setUndoing(true)}
            >
              <Undo2 size={13} aria-hidden />
              Undo {STEP_PHRASE[line.fulfillment]}
            </button>
          ) : null}
        </div>
      ) : null}

      <AdvanceSheet detail={detail} line={line} to={advancing} onClose={() => setAdvancing(null)} />
      <TrackingSheet detail={detail} line={line} open={tracking} onClose={() => setTracking(false)} />
      <ReasonDialog
        open={undoing}
        title={`Undo ${STEP_PHRASE[line.fulfillment]} for ${lineName(line)}?`}
        placeholder="Like: marked the wrong line"
        confirmLabel="Undo it"
        pendingLabel="Undoing…"
        pending={undo.isPending}
        error={undo.error?.message}
        onClose={() => {
          undo.reset()
          setUndoing(false)
        }}
        onConfirm={(reason) => undo.mutate(reason, { onSuccess: () => setUndoing(false) })}
      >
        It goes back to {back ? STEP_PHRASE[back] : 'where it was'}
        {line.fulfillment === 'picked_up' && line.rackItemId ? ', and the piece is held for the customer again' : ''}
        {line.fulfillment === 'picked_up' && detail.order.status === 'completed' ? '. The order opens again' : ''}.
      </ReasonDialog>
    </div>
  )
}

/**
 * Mark the next step: tperent's `PickupForm`, one line at a time. The day,
 * today unless it happened earlier; the supplier's number when marking it
 * ordered. For a pickup, the money is right there with Record payment beside
 * it — and when the API refuses because a balance is due, an owner can let
 * it go home anyway, saying why.
 */
function AdvanceSheet({
  detail,
  line,
  to,
  onClose,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly to: FulfillmentTarget | null
  readonly onClose: () => void
}) {
  return (
    <FormSheet
      open={to !== null}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title={to ? `${ADVANCE_LABEL[to]} — ${lineName(line)}` : ''}
      description={`${detail.order.orderNo} · ${detail.order.customerName}`}
    >
      {to ? <AdvanceForm detail={detail} line={line} to={to} onDone={onClose} /> : null}
    </FormSheet>
  )
}

function AdvanceForm({
  detail,
  line,
  to,
  onDone,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly to: FulfillmentTarget
  readonly onDone: () => void
}) {
  const { token, role } = useStaffToken()
  const advance = useAdvanceOrderLine(token, detail.order.id, line.id)
  const override = useOverridePickup(token, detail.order.id, line.id)
  const link = useLinkSupplierItem(token)
  const today = boutiqueToday()
  const [on, setOn] = useState(today)
  const [supplierOrderNo, setSupplierOrderNo] = useState(line.supplierOrderNo ?? '')
  const [item, setItem] = useState<SupplierItem | null>(null)
  // A line on its way from the supplier, not linked yet: its Jovita item can be picked rather than its number typed.
  const picksFromJovita = to === 'ordered' && !line.supplierLink
  const [reason, setReason] = useState('')
  const [recording, setRecording] = useState(false)
  const pickup = to === 'picked_up'
  const balance = detail.money.balanceMinor
  const blocked = advance.error?.code === 'balance_due'
  const needsNumber = to === 'ordered' && !item && !supplierOrderNo.trim()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (needsNumber) return
    if (item) {
      // Linking fills the factory number and moves the line as far as Jovita says it has come.
      const linked = await link.mutateAsync({ orderId: detail.order.id, lineId: line.id, itemId: item.id }).catch(() => null)
      if (!linked) return
      const now = linked.lines.find((l) => l.id === line.id)
      if (!now || now.fulfillment !== 'to_order') return onDone()
      const number = now.supplierOrderNo ?? item.factoryNo ?? (item.itemNo !== null ? String(item.itemNo) : '')
      advance.mutate({ to, ...(on !== today ? { on } : {}), supplierOrderNo: number }, { onSuccess: onDone })
      return
    }
    advance.mutate(
      { to, ...(on !== today ? { on } : {}), ...(to === 'ordered' ? { supplierOrderNo: supplierOrderNo.trim() } : {}) },
      { onSuccess: onDone },
    )
  }

  // The record-payment sheet sits outside the form: portalled or not, React
  // bubbles its submit up the component tree into this one (tperent found it).
  return (
    <>
      <form onSubmit={(event) => void submit(event)}>
        {pickup ? (
          <div className="st-money-line mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="pickup-money">
            <PaymentStateBadge state={detail.money.paymentState} totalMinor={detail.money.totalMinor} paidMinor={detail.money.paidMinor} />
            <span className="tabular-nums">
              {balance > 0 ? `${formatCents(balance)} due` : 'Nothing due'}
            </span>
            {balance > 0 ? (
              <button type="button" className="st-btn-ghost st-btn-sm ml-auto" onClick={() => setRecording(true)}>
                Record payment
              </button>
            ) : null}
          </div>
        ) : null}
        {pickup && balance > 0 && !blocked ? (
          <p className="st-notice mb-3">It can go home once the balance is paid. Take the payment first, then mark it picked up.</p>
        ) : null}

        {picksFromJovita ? (
          <JovitaItemField
            lineId={line.id}
            itemId={item?.id ?? ''}
            typed={supplierOrderNo}
            onPick={setItem}
            onType={setSupplierOrderNo}
            typedLabel="Supplier order number"
            startTyping={Boolean(line.supplierOrderNo)}
          />
        ) : to === 'ordered' ? (
          <FormField label="Supplier order number">
            <input
              className="st-input"
              maxLength={64}
              autoComplete="off"
              autoFocus
              value={supplierOrderNo}
              onChange={(event) => setSupplierOrderNo(event.target.value)}
            />
          </FormField>
        ) : null}
        <div className="mb-3 flex flex-col gap-1">
          <span className="st-eyebrow">{STEP_DAY_LABEL[to]}</span>
          <div>
            <DatePicker value={on} onChange={setOn} label={STEP_DAY_LABEL[to]} max={today} />
          </div>
          {on !== today ? <span className="st-meta text-xs">Earlier than today: it&rsquo;s recorded for that day.</span> : null}
        </div>

        {advance.isError || link.isError ? (
          <p className="st-alert mb-3" role="alert">
            {(link.error ?? advance.error)?.message}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="st-btn-ghost" onClick={onDone}>
            Not now
          </button>
          <button type="submit" className="st-btn" disabled={advance.isPending || link.isPending || needsNumber}>
            {advance.isPending || link.isPending ? 'Saving…' : ADVANCE_LABEL[to]}
          </button>
        </div>
      </form>

      {blocked && role === 'owner' ? (
        <form
          className="mt-5 border-t pt-4"
          style={{ borderColor: 'var(--st-rule)' }}
          aria-label="Let it go home with a balance"
          onSubmit={(event) => {
            event.preventDefault()
            if (reason.trim()) override.mutate({ reason: reason.trim(), ...(on !== today ? { on } : {}) }, { onSuccess: onDone })
          }}
        >
          <h3 className="st-display mb-1 text-base">Let it go home anyway?</h3>
          <p className="st-meta mb-3 text-sm">
            As the owner you can hand it over with {formatCents(balance)} still due. The order stays open, shows the balance
            as an alert until it&rsquo;s paid, and your reason goes on its history.
          </p>
          <label className="flex flex-col gap-1">
            <span className="st-eyebrow">Reason</span>
            <textarea
              className="st-input"
              rows={2}
              required
              maxLength={500}
              placeholder="Like: paying the rest by Zelle on Friday"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {override.isError ? (
            <p className="st-alert mt-3" role="alert">
              {override.error.message}
            </p>
          ) : null}
          <div className="mt-3 flex justify-end">
            <button type="submit" className="st-btn" style={{ background: '#8d3f2c', borderColor: '#8d3f2c' }} disabled={!reason.trim() || override.isPending}>
              {override.isPending ? 'Saving…' : `Picked up with ${formatCents(balance)} due`}
            </button>
          </div>
        </form>
      ) : blocked ? (
        <p className="st-meta mt-3 text-xs">Only an owner can let it go home with a balance due.</p>
      ) : null}

      <RecordPaymentDialog
        detail={detail}
        open={recording}
        onClose={() => {
          setRecording(false)
          // The balance it was refused on is not the balance now.
          advance.reset()
        }}
      />
    </>
  )
}

/**
 * What is known about a line on its way: the supplier's number and the day
 * it went in, the ETA staff type — the supplier link's is shown and never
 * typed over — and, for a piece on hold, the day it's held until. A line not
 * linked to Jovita yet picks its Jovita item here (`JovitaItemField`), or
 * types the number by hand.
 */
function TrackingSheet({
  detail,
  line,
  open,
  onClose,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly open: boolean
  readonly onClose: () => void
}) {
  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title={isStockLine(line) ? `Held until — ${lineName(line)}` : `Supplier & ETA — ${lineName(line)}`}
      description={`${detail.order.orderNo} · ${detail.order.customerName}`}
    >
      {open ? <TrackingForm detail={detail} line={line} onDone={onClose} /> : null}
    </FormSheet>
  )
}

function TrackingForm({ detail, line, onDone }: { readonly detail: OrderDetailResponse; readonly line: OrderLineView; readonly onDone: () => void }) {
  const { token } = useStaffToken()
  const save = useUpdateLineTracking(token, detail.order.id, line.id)
  const link = useLinkSupplierItem(token)
  const today = boutiqueToday()
  const supplier = isSupplierLine(line)
  const ordered = line.fulfillment !== 'to_order'
  const [supplierOrderNo, setSupplierOrderNo] = useState(line.supplierOrderNo ?? '')
  const [item, setItem] = useState<SupplierItem | null>(null)
  const [orderedOn, setOrderedOn] = useState(line.supplierOrderedAt ? boutiqueDayOf(line.supplierOrderedAt) : '')
  const [manualEta, setManualEta] = useState(line.manualEta ?? '')
  const [heldUntil, setHeldUntil] = useState(line.heldUntil ?? '')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (item) {
      // Linked, the line takes Jovita's number, ETA and steps; what else was changed here follows.
      const linked = await link.mutateAsync({ orderId: detail.order.id, lineId: line.id, itemId: item.id }).catch(() => null)
      if (!linked) return
    }
    // Only what changed: the log should say what somebody did, not what they re-saved.
    const body: UpdateLineTrackingRequest = {}
    if (supplier) {
      const number = supplierOrderNo.trim()
      if (!item && number !== (line.supplierOrderNo ?? '')) body.supplierOrderNo = number || null
      if (!item && ordered && orderedOn && orderedOn !== (line.supplierOrderedAt ? boutiqueDayOf(line.supplierOrderedAt) : '')) body.supplierOrderedOn = orderedOn
      if (manualEta !== (line.manualEta ?? '')) body.manualEta = manualEta || null
    } else if (heldUntil !== (line.heldUntil ?? '')) {
      body.heldUntil = heldUntil || null
    }
    if (Object.keys(body).length === 0) return onDone()
    save.mutate(body, { onSuccess: onDone })
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      {supplier ? (
        <>
          {line.supplierLink ? (
            <FormField label="Supplier order number" hint="Filled from Jovita. It can be corrected, not cleared.">
              <input className="st-input" maxLength={64} autoComplete="off" value={supplierOrderNo} onChange={(event) => setSupplierOrderNo(event.target.value)} />
            </FormField>
          ) : (
            <JovitaItemField
              lineId={line.id}
              itemId={item?.id ?? ''}
              typed={supplierOrderNo}
              onPick={setItem}
              onType={setSupplierOrderNo}
              typedLabel="Supplier order number"
              typedHint={ordered ? 'Once the line is ordered, the number can be corrected, not cleared.' : undefined}
              startTyping={Boolean(line.supplierOrderNo)}
            />
          )}
          {ordered && !item ? (
            <div className="mb-3 flex flex-col gap-1">
              <span className="st-eyebrow">Ordered on</span>
              <div>
                <DatePicker value={orderedOn} onChange={setOrderedOn} label="Ordered on" max={today} />
              </div>
            </div>
          ) : null}
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">ETA</span>
            <div className="flex items-center">
              <DatePicker value={manualEta} onChange={setManualEta} label="ETA" placeholder="No ETA yet" onClear={() => setManualEta('')} />
            </div>
            <span className="st-meta text-xs">
              {line.systemEta
                ? `The supplier says ${formatDay(line.systemEta, BOUTIQUE_LOCALE)}. An ETA here counts over it; clear it to go back to the supplier's.`
                : 'The date the supplier gave you. It counts for the alerts.'}
            </span>
          </div>
        </>
      ) : (
        <div className="mb-3 flex flex-col gap-1">
          <span className="st-eyebrow">Held until</span>
          <div className="flex items-center">
            <DatePicker value={heldUntil} onChange={setHeldUntil} label="Held until" placeholder="No date agreed" onClear={() => setHeldUntil('')} />
          </div>
          <span className="st-meta text-xs">The day agreed with the customer. Without one, the alert comes after the boutique&rsquo;s hold limit.</span>
        </div>
      )}
      {save.isError || link.isError ? (
        <p className="st-alert mb-3" role="alert">
          {(link.error ?? save.error)?.message}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="st-btn-ghost" onClick={onDone}>
          Not now
        </button>
        <button type="submit" className="st-btn" disabled={save.isPending || link.isPending}>
          {save.isPending || link.isPending ? 'Saving…' : item ? 'Link and save' : 'Save'}
        </button>
      </div>
    </form>
  )
}

/**
 * A line as the shop names it: its style number, the description only when
 * there is none — the ClickUp import wrote every gown as "Gown", which titled
 * every sheet here "Supplier & ETA — Gown".
 */
function lineName(line: Pick<OrderLineView, 'styleCode' | 'description'>): string {
  return line.styleCode || line.description
}
